import { Autorenew } from '@mui/icons-material';
import {
  Button, Tooltip, Typography, useTheme
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import OkCancelConfirmDialog from '../ConfirmDialog/OkCancelConfirmDialog';
import YesNoConfirmDialog from '../ConfirmDialog/YesNoConfirmDialog';
import Count from './Count';
import commonButtonStyles from './ETOButtonCommonStyles';
import { ETOButtonProps } from './ETOButtonProps';
import IconWrapper from './IconWrapper';

const ETOButton: React.FC<React.PropsWithChildren<ETOButtonProps>> = (props) => {
  const {
    customSx,
    disabled,
    loading,
    children,
    buttonProps,
    icon,
    loadingText,
    color,
    size,
    count,
    onClick,
    type,
    confirm,
    tooltip,
  } = props;
  const theme = useTheme();
  const commonStyles = commonButtonStyles(theme);

  const [dialogOpen, setDialogOpen] = useState(false);

  const _onDialogLeft = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      setDialogOpen(false);
      if (confirm && confirm.type === 'yesNo') {
        confirm.onNo(e);
      } else if (confirm && confirm.type === 'custom') {
        confirm.leftButton?.onClick(e, undefined);
      }
    },
    [confirm],
  );

  const _onDialogRight = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      if (onClick) {
        onClick(e);
        setDialogOpen(false);
      }
    },
    [onClick],
  );

  const loadText = loadingText ?? 'Loading...';

  const _renderConfirm = useCallback(() => {
    if (confirm) {
      switch (confirm.type) {
        case 'yesNo':
          return (
            <YesNoConfirmDialog
              open={dialogOpen}
              title={confirm.title}
              content={confirm.content}
              data={confirm.data}
              onYes={_onDialogRight}
              onNo={_onDialogLeft}
            />
          );
        case 'okCancel':
          return (
            <OkCancelConfirmDialog
              open={dialogOpen}
              title={confirm.title}
              content={confirm.content}
              data={confirm.data}
              onOk={_onDialogRight}
              onCancel={() => _onDialogLeft(undefined)}
            />
          );
        case 'custom':
          // eslint-disable-next-line react/jsx-props-no-spreading
          return <ConfirmDialog {...confirm} open={dialogOpen} />;
        default:
          // eslint-disable-next-line react/jsx-no-useless-fragment
          return <></>;
      }
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }, [_onDialogLeft, _onDialogRight, confirm, dialogOpen]);

  const _onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      if (onClick) {
        if (confirm) {
          setDialogOpen(true);
        } else {
          onClick(e);
        }
      }
    },
    [onClick, setDialogOpen, confirm],
  );

  return (
    <>
      <Tooltip arrow title={tooltip || ''}>
        <Button
          type={type}
        // eslint-disable-next-line react/jsx-props-no-spreading
          {...buttonProps}
          disabled={disabled || loading}
          sx={[
            { ...commonStyles[size] },
            !loading && (disabled ? commonStyles.disabled : commonStyles[color]),
            ...(Array.isArray(customSx) ? customSx : [customSx]),
          ]}
          onClick={(e) => _onClick(e)}
          onTouchStart={(e:any) => _onClick(e)}
          startIcon={
          icon && (
            <IconWrapper loading={loading} size={size}>
              {loading ? <Autorenew /> : icon}
            </IconWrapper>
          )
        }
          data-testid="button"
          tabIndex={0}
        >
          {loading && (
          <Typography data-testid="button-text">{loadText}</Typography>
          )}
          {!loading && children}
          {!loading && typeof count === 'number' && children && (
          <Count count={count} color={color} />
          )}
        </Button>
      </Tooltip>
      {confirm && _renderConfirm()}
    </>
  );
};

export default ETOButton;
